// src/redux/exitFlowSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  eligibilityCall,
  statusCall,
  initiateExitCall,
  montageCompleteCall,
  detailsCall,
} from "../../api/ExitApis/initiateExitApi";

/* ------- tiny local storage helpers for resumeToken & tier ------- */
const STORAGE_KEY = "EXIT_RESUME_TOKEN";
const TIER_KEY = "EXIT_TIER";

const loadToken = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) || null;
  } catch {
    return null;
  }
};
const saveToken = (t) => {
  try {
    t
      ? localStorage.setItem(STORAGE_KEY, t)
      : localStorage.removeItem(STORAGE_KEY);
  } catch {}
};

const loadTier = () => {
  try {
    return localStorage.getItem(TIER_KEY) || null;
  } catch {
    return null;
  }
};
const saveTier = (tier) => {
  try {
    tier
      ? localStorage.setItem(TIER_KEY, tier)
      : localStorage.removeItem(TIER_KEY);
  } catch {}
};

/* ------- THUNKS (params carry creds) ------- */
// Params: { systemName: string, authToken: string }

// 1) Plain eligibility with robust error reporting
export const checkEligibility = createAsyncThunk(
  "exit/checkEligibility",
  async ({ systemName, authToken }, { rejectWithValue }) => {
    try {
      const res = await eligibilityCall(systemName, authToken);
      if (!res?.eligible) {
        return rejectWithValue({
          code: "NOT_ELIGIBLE",
          reason: res?.reason || "min_2_levels",
          data: res,
        });
      }
      return res; // { eligible:true, ... }
    } catch (err) {
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Eligibility request failed";
      return rejectWithValue({ code: "REQUEST_FAILED", status, message: msg });
    }
  }
);

// 2) Gate + init with robust error reporting and broader init condition
export const ensureEligibleThenInitialize = createAsyncThunk(
  "exit/ensureEligibleThenInitialize",
  async ({ systemName, authToken }, { getState, rejectWithValue }) => {
    // const resumeToken = getState().exitFlow?.resumeToken || null;

    try {
      // a) eligibility
      const elig = await eligibilityCall(systemName, authToken);
      if (!elig?.eligible) {
        return rejectWithValue({
          code: "NOT_ELIGIBLE",
          reason: elig?.reason || "min_2_levels",
          data: elig,
        });
      }
      return elig;
      // const needsInitiate =
      //   !status?.state ||
      //   status.state === "new" ||
      //   status.state === "not_started" ||
      //   status.alreadyInitiated === false;

      // if (needsInitiate) {
      //   const init = await initiateExitCall(systemName, authToken, resumeToken);
      //   return init || status;
      // }
    } catch (err) {
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Exit initialization failed";
      return rejectWithValue({ code: "REQUEST_FAILED", status, message: msg });
    }
  }
);

export const ensureExitInitializedOnly = createAsyncThunk(
  "exit/ensureExitInitializedOnly",
  async ({ systemName, authToken }, { getState, rejectWithValue }) => {
    const resumeToken = getState().exitFlow?.resumeToken || null;
    try {
      const status = await statusCall(systemName, authToken, resumeToken);

      const needsInitiate =
        !status?.state ||
        status.state === "new" ||
        status.state === "not_started" ||
        status.alreadyInitiated === false;

      if (needsInitiate) {
        const init = await initiateExitCall(systemName, authToken, resumeToken);
        return init || status;
      }
      return status;
    } catch (err) {
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Exit initialization failed";
      return rejectWithValue({ code: "REQUEST_FAILED", status, message: msg });
    }
  }
);
// send montage
export const montageComplete = createAsyncThunk(
  "exit/montageComplete",
  async ({ systemName, authToken }, { getState, rejectWithValue }) => {
    const resumeToken = getState().exitFlow?.resumeToken || null;
    try {
      const res = await montageCompleteCall(systemName, authToken, resumeToken);
      return res; // expect { state: "montage_done", ... }
    } catch (err) {
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Montage completion failed";
      return rejectWithValue({ code: "REQUEST_FAILED", status, message: msg });
    }
  }
);
// submit the form
export const submitDetails = createAsyncThunk(
  "exit/details",
  async (
    { systemName, authToken, name, email, linkedin },
    { getState, rejectWithValue }
  ) => {
    const resumeToken = getState().exitFlow?.resumeToken || null;
    try {
      // backend expects { name, email, linkedin, resumeToken } in body
      const res = await detailsCall(systemName, authToken, resumeToken, {
        name,
        email,
        linkedin,
      });
      return res; // expect { state: "details_submitted", ... }
    } catch (err) {
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Submitting details failed";
      return rejectWithValue({ code: "REQUEST_FAILED", status, message: msg });
    }
  }
);

// --- STATUS ONLY (no initiation) ---
export const fetchExitStatusOnly = createAsyncThunk(
  "exit/statusOnly",
  async ({ systemName, authToken }, { getState, rejectWithValue }) => {
    const resumeToken = getState().exitFlow?.resumeToken || null;
    try {
      const res = await statusCall(systemName, authToken, resumeToken);
      return res; // { state, resumeToken?, lockInfo? ... }
    } catch (err) {
      const status = err?.response?.status;
      const message =
        err?.response?.data?.message || err?.message || "Status request failed";
      return rejectWithValue({ code: "REQUEST_FAILED", status, message });
    }
  }
);

/* ------- SLICE ------- */
const exitFlowSlice = createSlice({
  name: "exitFlow",
  initialState: {
    state: null,
    resumeToken: loadToken(),
    tier: loadTier(), // <--- load from storage
    eligible: null,
    ineligibleReason: null,
    lockInfo: null,
    loading: false,
    error: null,
  },

  reducers: {
    resetExitFlow(state, action) {
      state.state = null;
      state.eligible = null;
      state.ineligibleReason = null;
      state.lockInfo = null;
      state.error = null;
      if (action?.payload?.clearToken) {
        state.resumeToken = null;
        saveToken(null);
      }
    },
  },
  extraReducers: (b) => {
    const start = (s) => {
      s.loading = true;
      s.error = null;
    };
    // const fail = (s, a) => {
    //   s.loading = false;
    //   s.error = a.error?.message || "Request failed";
    // };

    // checkEligibility
    b.addCase(checkEligibility.pending, start);
    b.addCase(checkEligibility.fulfilled, (s, { payload }) => {
      s.loading = false;
      s.eligible = true;
      s.ineligibleReason = null;

      // if backend returns lockInfo/state even here, capture it
      if (payload?.lockInfo) s.lockInfo = payload.lockInfo;
      if (payload?.state) s.state = payload.state;
      if (payload?.resumeToken && payload.resumeToken !== s.resumeToken) {
        s.resumeToken = payload.resumeToken;
        saveToken(payload.resumeToken);
      }
    });
    b.addCase(checkEligibility.rejected, (s, a) => {
      s.loading = false;
      if (a.payload?.code === "NOT_ELIGIBLE") {
        s.eligible = false;
        s.ineligibleReason = a.payload.reason;
        s.error = null;
      } else {
        s.error = a.error?.message || "Request failed";
      }
    });

    b.addCase(ensureExitInitializedOnly.pending, (s) => {
      s.loading = true;
      s.error = null;
    });
    b.addCase(ensureExitInitializedOnly.fulfilled, (s, { payload }) => {
      s.loading = false;
      if (payload?.resumeToken && payload.resumeToken !== s.resumeToken) {
        s.resumeToken = payload.resumeToken;
        saveToken(payload.resumeToken);
      }
      if (payload?.state) s.state = payload.state;
      if (payload?.lockInfo) s.lockInfo = payload.lockInfo;
    });
    b.addCase(ensureExitInitializedOnly.rejected, (s, a) => {
      s.loading = false;
      s.error = a.payload?.message || a.error?.message || "Request failed";
    });
    // montage don
    b.addCase(montageComplete.pending, (s) => {
      s.loading = true;
      s.error = null;
    });
    b.addCase(montageComplete.fulfilled, (s, { payload }) => {
      s.loading = false;
      if (payload?.resumeToken && payload.resumeToken !== s.resumeToken) {
        s.resumeToken = payload.resumeToken;
        saveToken(payload.resumeToken);
      }
      if (payload?.state) s.state = payload.state; // should become "montage_done"
      if (payload?.coinManifest !== undefined)
        s.coinManifest = payload.coinManifest;
      if (payload?.tier !== undefined) {
        s.tier = payload.tier;
        saveTier(payload.tier); // <--- persist tier
      }
      if (payload?.lockInfo) s.lockInfo = payload.lockInfo;
    });
    b.addCase(montageComplete.rejected, (s, a) => {
      s.loading = false;
      s.error = a.payload?.message || a.error?.message || "Request failed";
    });
    // submit form
    b.addCase(submitDetails.pending, (s) => {
      s.loading = true;
      s.error = null;
    });
    b.addCase(submitDetails.fulfilled, (s, { payload }) => {
      s.loading = false;
      if (payload?.resumeToken && payload.resumeToken !== s.resumeToken) {
        s.resumeToken = payload.resumeToken;
        saveToken(payload.resumeToken);
      }
      if (payload?.state) s.state = payload.state; // should be "details_submitted"
      if (payload?.lockInfo) s.lockInfo = payload.lockInfo;
      if (payload?.tier !== undefined) s.tier = payload.tier;
      if (payload?.coinManifest !== undefined)
        s.coinManifest = payload.coinManifest;
    });
    b.addCase(submitDetails.rejected, (s, a) => {
      s.loading = false;
      s.error = a.payload?.message || a.error?.message || "Request failed";
    });
    // check status only
    b.addCase(fetchExitStatusOnly.pending, (s) => {
      s.loading = true;
      s.error = null;
    });
    b.addCase(fetchExitStatusOnly.fulfilled, (s, { payload }) => {
      s.loading = false;
      if (payload?.resumeToken && payload.resumeToken !== s.resumeToken) {
        s.resumeToken = payload.resumeToken;
        saveToken(payload.resumeToken);
      }
      if (payload?.state) s.state = payload.state;
      if (payload?.lockInfo) s.lockInfo = payload.lockInfo;
    });
    b.addCase(fetchExitStatusOnly.rejected, (s, a) => {
      s.loading = false;
      s.error = a.payload?.message || a.error?.message || "Request failed";
    });

    // ensureEligibleThenInitialize
    b.addCase(ensureEligibleThenInitialize.pending, start);
    b.addCase(ensureEligibleThenInitialize.fulfilled, (s, { payload }) => {
      s.loading = false;
      s.eligible = true;
      s.ineligibleReason = null;

      if (payload?.resumeToken && payload.resumeToken !== s.resumeToken) {
        s.resumeToken = payload.resumeToken;
        saveToken(payload.resumeToken);
      }
      if (payload?.state) s.state = payload.state;
      if (payload?.lockInfo) s.lockInfo = payload.lockInfo; // <-- persist lock info
    });
    b.addCase(ensureEligibleThenInitialize.rejected, (s, a) => {
      s.loading = false;
      if (a.payload?.code === "NOT_ELIGIBLE") {
        s.eligible = false;
        s.ineligibleReason = a.payload.reason;
        s.error = null; // not an error; just ineligible
      } else {
        s.error = a.error?.message || "Request failed";
      }
    });
  },
});

export const { resetExitFlow } = exitFlowSlice.actions;
export default exitFlowSlice.reducer;

/* ------- SELECTORS ------- */
export const selectExitState = (s) => s.exitFlow?.state;
export const selectExitLockedInfo = (s) => s.exitFlow?.lockInfo;
export const selectIsExitLocked = (s) => {
  const state = s.exitFlow?.state;
  const locked = s.exitFlow?.lockInfo?.gameLocked === true;
  const exitToken = s.exitFlow?.resumeToken;
  // treat initiated and beyond as "locked" (tweak as needed)
  return (
    locked ||
    exitToken ||
    state === "initiated" ||
    state === "reward_ready" ||
    state === "finalized" ||
    state === "montage_done" ||
    state === "details_submitted" ||
    state === "reveal_ready" ||
    state === "reveal_done"
  );
};
